import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { getAuthHeaders } from '../../utils/auth';
import { reportReasonLabel } from '../../utils/moderation';

// Stored in UTC without an offset.
const parseUtc = (value) => parseISO(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);
const when = (value) => format(parseUtc(value), 'dd.MM.yyyy HH:mm');

const FILTERS = { open: false, resolved: true, all: null };

/**
 * Members reporting other members ("Zgłoś" in the app and on the website).
 * Each report is also emailed to the studio. Apple expects reports to be
 * acted on, so open ones stay on top until an admin marks them resolved.
 * The reporter can block the person themselves; there is no admin ban yet.
 */
const UserReportsManagement = () => {
  const [filter, setFilter] = useState('open');
  const [page, setPage] = useState(null);
  const [notes, setNotes] = useState({});
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const resolved = FILTERS[filter];
      const query = resolved === null ? '' : `?resolved=${resolved}`;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/reports${query}`, { headers });
      if (!response.ok) {
        setError(response.status === 403 || response.status === 401 ? 'Brak uprawnień do zgłoszeń' : 'Nie udało się pobrać zgłoszeń');
        return;
      }
      setPage(await response.json());
      setError('');
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (report, resolved) => {
    setBusyId(report.id);
    try {
      const headers = await getAuthHeaders();
      const note = notes[report.id];
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/reports/${report.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ resolved, ...(note !== undefined ? { admin_note: note } : {}) }),
      });
      if (!response.ok) throw new Error();
      await load();
    } catch (err) {
      setError('Nie udało się zapisać zgłoszenia');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5">
          Zgłoszenia {page?.open_count ? <Chip size="small" color="error" label={`${page.open_count} otwarte`} /> : null}
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="open">Otwarte</ToggleButton>
          <ToggleButton value="resolved">Załatwione</ToggleButton>
          <ToggleButton value="all">Wszystkie</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!page && !error && <CircularProgress />}
      {page && !page.items.length && <Typography color="text.secondary">Brak zgłoszeń.</Typography>}

      <Stack spacing={2}>
        {page?.items.map((r) => (
          <Card key={r.id} variant="outlined" data-testid="user-report">
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                <Chip size="small" color="warning" label={reportReasonLabel(r.reason)} />
                {r.post_id && <Chip size="small" variant="outlined" label={`Wpis na Tablicy #${r.post_id}`} />}
                {r.comment_id && <Chip size="small" variant="outlined" label={`Komentarz na Tablicy #${r.comment_id}`} />}
                {r.blocked_by_reporter && <Chip size="small" variant="outlined" label="Zgłaszający zablokował" />}
                {r.reports_about_reported > 1 && (
                  <Chip size="small" color="error" variant="outlined" label={`Zgłoszeń tej osoby: ${r.reports_about_reported}`} />
                )}
                {r.resolved_at && <Chip size="small" color="success" label="Załatwione" />}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {when(r.created_at)}
                </Typography>
              </Box>
              <Typography variant="body2">
                <b>Zgłoszona osoba:</b> {r.reported.full_name} {r.reported.email ? `(${r.reported.email})` : ''}
              </Typography>
              <Typography variant="body2">
                <b>Zgłaszający:</b> {r.reporter.full_name} {r.reporter.email ? `(${r.reporter.email})` : ''}
              </Typography>
              {r.post_id && (
                <Typography variant="body2">
                  <b>Notatka we wpisie:</b> {r.post_note ? `„${r.post_note}”` : '(brak)'}
                </Typography>
              )}
              {r.comment_id && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  <b>Komentarz:</b> „{r.comment_body}”
                </Typography>
              )}
              {r.details && (
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  {r.details}
                </Typography>
              )}
              {r.resolved_at && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Załatwione {when(r.resolved_at)}{r.resolved_by ? ` przez ${r.resolved_by.full_name}` : ''}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Notatka studia"
                  value={notes[r.id] ?? r.admin_note ?? ''}
                  onChange={(e) => setNotes((prev) => ({ ...prev, [r.id]: e.target.value }))}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                {r.resolved_at ? (
                  <Button disabled={busyId === r.id} onClick={() => resolve(r, false)}>
                    Otwórz ponownie
                  </Button>
                ) : (
                  <Button variant="contained" disabled={busyId === r.id} onClick={() => resolve(r, true)}>
                    Oznacz jako załatwione
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default UserReportsManagement;
